import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FaTags } from '@react-icons/all-files/fa/FaTags';
import { FaProjectDiagram } from '@react-icons/all-files/fa/FaProjectDiagram';
import { FaCog } from '@react-icons/all-files/fa/FaCog';
import { FaHome } from '@react-icons/all-files/fa/FaHome';
import Breadcrumb from '../../../../commons/Breadcrumb';
import PageHeader from '../../../../commons/PageHeader';
import PageForm, { FormField } from '../../../../commons/PageForm';
import { useAppDispatch, useAppSelector } from '../../../../store';
import { getEntity, createEntity, updateEntity, reset } from '../../../../manager/tipo-actividad.reducer';

const BACK = '/configuracion/workflow/tipos-actividad';

interface TipoActividadForm {
  id: string;
  descripcion: string;
}

const empty: TipoActividadForm = { id: '', descripcion: '' };

const fields: FormField<TipoActividadForm>[] = [
  {
    name: 'id',
    label: 'ID',
    readOnly: true,
    editOnly: true,
  },
  {
    name: 'descripcion',
    label: 'Descripción',
    type: 'textarea',
    rows: 3,
    placeholder: 'Descripción del tipo de actividad',
  },
];

export default function TipoActividadFormPage() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id;
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const entity = useAppSelector(s => s.tipoActividad.entity) as any;
  const updating = useAppSelector(s => s.tipoActividad.updating);
  const updateSuccess = useAppSelector(s => s.tipoActividad.updateSuccess);

  const [form, setForm] = useState<TipoActividadForm>(empty);
  const [errors, setErrors] = useState<Partial<Record<keyof TipoActividadForm, string>>>({});

  useEffect(() => {
    if (!isNew) {
      dispatch(getEntity(id!));
    } else {
      dispatch(reset());
      setForm(empty);
    }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isNew && entity?.id) {
      setForm({
        id: String(entity.id),
        descripcion: entity.descripcion ?? '',
      });
    }
  }, [entity]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (updateSuccess) {
      dispatch(reset());
      navigate(BACK, {
        state: {
          successMsg: isNew
            ? 'Tipo de actividad creado correctamente.'
            : 'Tipo de actividad actualizado correctamente.',
        },
      });
    }
  }, [updateSuccess]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFieldChange = (name: keyof TipoActividadForm, value: string) => {
    setForm(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: undefined }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errs: Partial<Record<keyof TipoActividadForm, string>> = {};
    if (Object.keys(errs).length) { setErrors(errs); return; }

    const payload = {
      ...(isNew ? {} : { id: Number(form.id) }),
      descripcion: form.descripcion || null,
    };

    if (isNew) {
      dispatch(createEntity(payload));
    } else {
      dispatch(updateEntity(payload));
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <Breadcrumb items={[
        { label: 'Inicio', to: '/', icon: <FaHome /> },
        { label: 'Configuración', to: '/configuracion', icon: <FaCog /> },
        { label: 'Workflow', to: '/configuracion/workflow', icon: <FaProjectDiagram /> },
        { label: 'Tipos de Actividad', to: BACK, icon: <FaTags /> },
        { label: isNew ? 'Nuevo Tipo' : `Editar Tipo #${id}` },
      ]} />

      <PageHeader
        icon={<FaTags />}
        iconColor="#8b5cf6"
        title={isNew ? 'Nuevo Tipo de Actividad' : 'Editar Tipo de Actividad'}
        subtitle={isNew
          ? 'Completa los campos para crear un nuevo tipo de actividad'
          : `Modificando tipo de actividad #${id}`}
      />

      <PageForm
        fields={fields}
        values={form}
        errors={errors}
        isNew={isNew}
        saving={updating}
        onFieldChange={handleFieldChange}
        onSubmit={handleSubmit}
        onCancel={() => navigate(BACK)}
        accentColor="#8b5cf6"
      />
    </div>
  );
}
